import { type Permission } from '../hooks/useAuthorization.hook';

/**
 * Metadata para rutas con información de autorización y configuración
 */
export interface RouteMetadata {
  title: string;
  description?: string;
  requiredPermissions?: Permission[];
  requireAnyPermission?: boolean;
  isPublic?: boolean;
  showInNavigation?: boolean;
  icon?: string;
  category?: string;
  priority?: number;
  beta?: boolean;
  deprecated?: boolean;
  breadcrumbLabel?: string;
  seoTitle?: string;
  seoDescription?: string;
  accessDeniedVariant?: 'default' | 'minimal' | 'detailed';
  loadingVariant?: 'circular' | 'linear' | 'pulsing' | 'dots';
  showContactInfo?: boolean;
}

/**
 * Definición de ruta extendida con metadata
 */
export interface RouteDefinition {
  path: string;
  element?: React.ReactNode;
  children?: RouteDefinition[];
  metadata: RouteMetadata;
  handle?: {
    crumb?: (data?: any) => string;
    [key: string]: any;
  };
}

/**
 * Configuración de rutas con metadata completa
 */
export const routesConfig: RouteDefinition[] = [
  // Rutas públicas
  {
    path: '/login',
    metadata: {
      title: 'Iniciar Sesión',
      description: 'Página de autenticación del portal',
      isPublic: true,
      showInNavigation: false,
      seoTitle: 'Portal de Acceso Digital - Iniciar Sesión',
      seoDescription: 'Acceso seguro al Portal Digital del Poder Judicial',
    }
  },
  {
    path: '/registro/*',
    metadata: {
      title: 'Registro de Usuario',
      description: 'Proceso de registro para nuevos usuarios',
      isPublic: true,
      showInNavigation: false,
      seoTitle: 'Registro - Portal de Acceso Digital',
      seoDescription: 'Registro de nuevos usuarios en el Portal Digital del Poder Judicial',
    }
  },

  // Rutas protegidas principales
  {
    path: '/',
    metadata: {
      title: 'Escritorio',
      description: 'Panel principal de usuario',
      showInNavigation: true,
      icon: 'RiDashboardLine',
      category: 'Principal',
      priority: 1,
      breadcrumbLabel: 'Inicio',
      accessDeniedVariant: 'detailed',
      showContactInfo: true,
    }
  },
  {
    path: '/caso/*',
    metadata: {
      title: 'Gestión de Casos',
      description: 'Administración y seguimiento de casos judiciales',
      requiredPermissions: [{ modulo: 'casos' }],
      showInNavigation: true,
      icon: 'RiFolderLine',
      category: 'Judicial',
      priority: 2,
      breadcrumbLabel: 'Casos',
      accessDeniedVariant: 'detailed',
      showContactInfo: true,
      loadingVariant: 'circular'
    },
    children: [
      {
        path: '/',
        metadata: {
          title: 'Lista de Casos',
          description: 'Vista general de todos los casos',
          breadcrumbLabel: 'Lista',
        }
      },
      {
        path: '/ver-tramites',
        metadata: {
          title: 'Ver Trámites',
          description: 'Visualización de trámites específicos',
          requiredPermissions: [{ modulo: 'casos', permiso: 'ver' }],
          breadcrumbLabel: 'Trámites',
        }
      },
      {
        path: '/detalles-tramite/:id',
        metadata: {
          title: 'Detalles del Trámite',
          description: 'Información detallada del trámite',
          requiredPermissions: [{ modulo: 'casos', permiso: 'ver' }],
          breadcrumbLabel: 'Detalles',
        },
        handle: {
          crumb: (data) => `Trámite #${data?.id || 'N/A'}`
        }
      }
    ]
  },
  {
    path: '/calendario',
    metadata: {
      title: 'Calendario de Audiencias',
      description: 'Programación y gestión de audiencias',
      requiredPermissions: [{ modulo: 'calendario' }],
      showInNavigation: true,
      icon: 'RiCalendarLine',
      category: 'Programación',
      priority: 3,
      breadcrumbLabel: 'Calendario',
      accessDeniedVariant: 'default',
    }
  },
  {
    path: '/archivo',
    metadata: {
      title: 'Archivo',
      description: 'Gestión de documentos y archivos',
      requiredPermissions: [{ modulo: 'archivo' }],
      showInNavigation: true,
      icon: 'RiArchiveLine',
      category: 'Archivos',
      priority: 4,
      breadcrumbLabel: 'Archivo',
      accessDeniedVariant: 'detailed',
      showContactInfo: true,
    }
  },
  {
    path: '/buzon-judicial',
    metadata: {
      title: 'Buzon Judicial',
      description: 'Gestion de autorizaciones y notificaciones del buzon judicial',
      requiredPermissions: [],
      showInNavigation: false,
      icon: 'RiMailLine',
      category: 'Judicial',
      priority: 5,
      breadcrumbLabel: 'Buzon Judicial',
      accessDeniedVariant: 'detailed',
      showContactInfo: true,
    }
  },

  // Rutas especializadas
  {
    path: '/vu/*',
    metadata: {
      title: 'Validación de Usuarios',
      description: 'Sistema de validación y verificación de usuarios',
      requiredPermissions: [{ modulo: 'validacion' }],
      showInNavigation: true,
      icon: 'RiUserCheckLine',
      category: 'Administración',
      priority: 10,
      beta: true,
      breadcrumbLabel: 'Validación',
      accessDeniedVariant: 'detailed',
      showContactInfo: true,
    },
    children: [
      {
        path: '/validacion-usuario',
        metadata: {
          title: 'Validar Usuario',
          description: 'Proceso de validación de usuario específico',
          breadcrumbLabel: 'Validar',
        }
      }
    ]
  }
];

/**
 * Utilidades para trabajar con las rutas configuradas
 */
export class RouteConfigManager {
  private static instance: RouteConfigManager;
  private routesMap: Map<string, RouteDefinition> = new Map();

  constructor() {
    this.buildRoutesMap(routesConfig);
  }

  static getInstance(): RouteConfigManager {
    if (!RouteConfigManager.instance) {
      RouteConfigManager.instance = new RouteConfigManager();
    }
    return RouteConfigManager.instance;
  }

  private buildRoutesMap(routes: RouteDefinition[], parentPath = ''): void {
    routes.forEach(route => {
      const fullPath = parentPath + route.path.replace('/*', '');
      this.routesMap.set(fullPath, route);
      
      if (route.children) {
        this.buildRoutesMap(route.children, fullPath);
      }
    });
  }

  /**
   * Obtener metadata de una ruta específica
   */
  getRouteMetadata(path: string): RouteMetadata | null {
    // Buscar coincidencia exacta primero
    const route = this.routesMap.get(path);
    if (route) return route.metadata;

    // Buscar coincidencia de patrón
    for (const [routePath, routeConfig] of this.routesMap.entries()) {
      if (this.matchesPattern(path, routePath)) {
        return routeConfig.metadata;
      }
    }

    return null;
  }

  /**
   * Obtener todas las rutas de navegación visibles
   */
  getNavigationRoutes(): Array<RouteDefinition & { fullPath: string }> {
    const navigationRoutes: Array<RouteDefinition & { fullPath: string }> = [];
    
    for (const [fullPath, route] of this.routesMap.entries()) {
      if (route.metadata.showInNavigation) {
        navigationRoutes.push({ ...route, fullPath });
      }
    }

    return navigationRoutes.sort((a, b) => {
      const priorityA = a.metadata.priority || 999;
      const priorityB = b.metadata.priority || 999;
      return priorityA - priorityB;
    });
  }

  /**
   * Obtener rutas por categoría
   */
  getRoutesByCategory(category: string): RouteDefinition[] {
    const routes: RouteDefinition[] = [];
    
    for (const route of this.routesMap.values()) {
      if (route.metadata.category === category) {
        routes.push(route);
      }
    }

    return routes;
  }

  /**
   * Obtener todas las categorías disponibles
   */
  getCategories(): string[] {
    const categories = new Set<string>();
    
    for (const route of this.routesMap.values()) {
      if (route.metadata.category) {
        categories.add(route.metadata.category);
      }
    }

    return Array.from(categories);
  }

  /**
   * Verificar si una ruta es pública
   */
  isPublicRoute(path: string): boolean {
    const metadata = this.getRouteMetadata(path);
    return metadata?.isPublic || false;
  }

  /**
   * Obtener permisos requeridos para una ruta
   */
  getRequiredPermissions(path: string): Permission[] {
    const metadata = this.getRouteMetadata(path);
    return metadata?.requiredPermissions || [];
  }

  /**
   * Generar breadcrumbs para una ruta
   */
  generateBreadcrumbs(path: string): Array<{ label: string; path: string }> {
    const segments = path.split('/').filter(Boolean);
    const breadcrumbs: Array<{ label: string; path: string }> = [];
    let currentPath = '';

    segments.forEach(segment => {
      currentPath += `/${segment}`;
      const metadata = this.getRouteMetadata(currentPath);
      
      if (metadata) {
        breadcrumbs.push({
          label: metadata.breadcrumbLabel || metadata.title,
          path: currentPath
        });
      }
    });

    return breadcrumbs;
  }

  private matchesPattern(path: string, pattern: string): boolean {
    /* Convertir patrón de ruta a RegExp */
    const regexPattern = pattern
      .replace(/:\w+/g, '[^/]+') // :id -> [^/]+
      .replace(/\*$/g, '.*');    // * -> .*
    
    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(path);
  }
}

// Instancia singleton
export const routeManager = RouteConfigManager.getInstance();
